import { useEffect, useState } from 'react'

import { getNotificationCategoriesUrl } from '../../../../constants/service-endpoints'
import { get } from '../../../../services/http.service'
import type { AppNotificationCategory, AppNotificationCategoriesResponse } from '../../../../services/notification'

export type NotificationsAccessState = 'loading' | 'permitted' | 'denied'

export interface NotificationsAccessResult {
  state: NotificationsAccessState
  categories: AppNotificationCategory[]
}

const fetchCategories = (
  setState: (s: NotificationsAccessState) => void,
  setCategories: (c: AppNotificationCategory[]) => void,
  errorMessage?: string,
): void => {
  get<AppNotificationCategoriesResponse>(getNotificationCategoriesUrl())
    .then(data => {
      const real = data.categories.filter(c => c.id !== 0)
      setCategories(data.categories)
      setState(real.length > 0 ? 'permitted' : 'denied')
    })
    .catch(error => {
      window.console.error(errorMessage ?? 'Failed to verify notification access', error)
      setState('denied')
    })
}

export const useNotificationsAccessGuard = (errorMessage?: string): NotificationsAccessResult => {
  const [customerId, setCustomerId] = useState<string>(
    () => sessionStorage.getItem('cop:customer-id') ?? ''
  )
  const [state, setState] = useState<NotificationsAccessState>('loading')
  const [categories, setCategories] = useState<AppNotificationCategory[]>([])

  // Re-fetch when active customer changes (covers account switch without page reload)
  useEffect(() => {
    const handleCustomerReady = (e: Event) => {
      const id = (e as CustomEvent<{ customerId?: string }>).detail?.customerId ?? ''
      setCustomerId(id)
    }
    window.addEventListener('cop:customer-ready', handleCustomerReady)
    return () => window.removeEventListener('cop:customer-ready', handleCustomerReady)
  }, [])

  useEffect(() => {
    if (!customerId) {
      setState('denied')
      return
    }
    setState('loading')
    fetchCategories(setState, setCategories, errorMessage)
  }, [customerId, errorMessage])

  return { state, categories }
}
